<script setup>
import { onBeforeMount, onBeforeUnmount, reactive, ref, watch } from "vue"
import _ from 'lodash'
import { useHttpErrorsHandler } from "../../Composables/httpErrorsHandler"
import { useLocalStorage } from "../../Composables/useLocalStorage.js"
import { useDataManager } from "../../Composables/useDataManager.js"
import { useHubSpotSyncIssues } from "../../Composables/useHubSpotSyncIssues.js"
import DataTable from "../../Components/DataTable.vue"
import FormSelectField from "../../Components/FormSelectField.vue"
import ModalHubSpotSyncIssueDetails from "../../Components/ModalHubSpotSyncIssueDetails.vue"

const { httpErrorsHandler } = useHttpErrorsHandler()

const { saveToLocalStorage, loadFromLocalStorage } = useLocalStorage("Shiftech_HubSpotSyncIssueList")

const filters = reactive({
    status: 'open',
    type: null,
})
const filtersApplied = ref(true)
const errors = ref(null)

const typeOptions = [
    { text: 'Tous les types', value: null },
    { text: 'Doublon dans HubSpot', value: 'email_on_other_contact' },
    { text: 'E-mail partagé', value: 'shared_email' },
    { text: 'Envoi en échec', value: 'sync_failed' },
]

const {
    url,
    data: issues,
    links,
    meta,
    loading,
    query,
    searchLoaded,
    sortingField,
    sortingFieldsOrder,
    changeSorting,
    search,
    loadData,
} = useDataManager({
    // Lignes hautes (explication et marche à suivre) : 10 par page pour rester lisible
    baseUrl: '/api/hubspot-sync-issues?per_page=10',
    baseSearchUrl: '/api/search/hubspot-sync-issues?per_page=10',
    defaultSortingField: 'last_seen_at',
    defaultSortingFieldsOrder: {
        last_seen_at: 'desc',
        occurrences: 'desc',
    },
    filters,
    onError: (error) => httpErrorsHandler(error),
})

const debouncedSearch = _.debounce(search, 250)

const columns = ref([
    { label: 'Fiche client', slot: 'id', class: 'col-1' },
    { label: 'Client', slot: 'client', class: 'col-2' },
    { label: 'Type', slot: 'type', class: 'col-1' },
    { label: 'Ce qui se passe', slot: 'summary', class: 'col-4', cellClass: 'col-4 text-wrap' },
    { label: 'Dernière fois', field: 'last_seen_at', sortable: true, slot: 'last_seen_at', class: 'col-1' },
    { label: 'Fois', field: 'occurrences', sortable: true, class: 'col-1 text-center' },
    { label: 'Détails', slot: 'details', class: 'col-1 text-center' },
    { label: 'Actions', slot: 'actions', class: 'col-1 text-center' },
])

const steps = [
    { icon: 'ri-search-eye-line', color: 'info', title: 'Comprendre le cas', text: 'Lisez ce qui se passe et ouvrez le détail pour accéder aux contacts HubSpot et aux fiches.' },
    { icon: 'ri-tools-line', color: 'warning', title: 'Corriger', text: 'Fusionnez les doublons dans HubSpot ou corrigez l\'e-mail sur la fiche client.' },
    { icon: 'ri-refresh-line', color: 'success', title: 'Relancer la synchro', text: 'Le cas disparaît tout seul si elle réussit. Sinon, marquez-le comme traité.' },
]

const selectedIssue = ref(null)

const { resolving, retrying, resolve, retry } = useHubSpotSyncIssues({
    onResolved: () => loadData(url.value),
    onError: (error) => httpErrorsHandler(error),
})

onBeforeMount(() => {
    const savedSettings = loadFromLocalStorage()
    if (savedSettings) {
        sortingField.value = savedSettings.sortingField || sortingField.value
        Object.assign(sortingFieldsOrder, savedSettings.sortingFieldsOrder || sortingFieldsOrder)
        query.value = savedSettings.query || ''
        searchLoaded.value = savedSettings.searchLoaded || false
        if (savedSettings.filters) {
            Object.assign(filters, savedSettings.filters)
        }
    }

    loadData()
})

watch(
    [sortingField, sortingFieldsOrder, query, searchLoaded],
    () => saveSettings(),
    { deep: true }
)

watch(
    () => [filters.status, filters.type],
    () => {
        filtersApplied.value = false
    }
)

onBeforeUnmount(() => {
    const modalInstance = bootstrap.Modal.getInstance(document.getElementById('modalHubSpotSyncIssue'))
    if (modalInstance) {
        modalInstance.hide()
    }
})

function saveSettings() {
    saveToLocalStorage({
        sortingField: sortingField.value,
        sortingFieldsOrder: { ...sortingFieldsOrder },
        query: query.value,
        searchLoaded: searchLoaded.value,
        filters: { ...filters },
    })
}

function applyFilter() {
    filtersApplied.value = true
    saveSettings()
    loadData()
}

function hideDetails() {
    bootstrap.Modal.getOrCreateInstance(document.getElementById('modalHubSpotSyncIssue')).hide()
}

async function retryFromModal(id) {
    await retry(id)
    hideDetails()
}

async function resolveFromModal(id) {
    await resolve(id)
    hideDetails()
}

function showDetails(issue) {
    selectedIssue.value = issue
    bootstrap.Modal.getOrCreateInstance(document.getElementById('modalHubSpotSyncIssue')).show()
}
</script>

<template>
    <div class="row">
        <div class="col-lg-12">
            <!-- Marche à suivre -->
            <div class="card">
                <div class="row g-0">
                    <div class="col-md-4 border-end" v-for="(step, index) in steps" :key="index">
                        <div class="p-3 d-flex align-items-center">
                            <div class="avatar-sm flex-shrink-0 me-3">
                                <span :class="'avatar-title rounded-circle fs-3 bg-' + step.color + '-subtle text-' + step.color">
                                    <i :class="step.icon"></i>
                                </span>
                            </div>
                            <div class="flex-grow-1">
                                <p class="text-uppercase fw-medium text-muted fs-12 mb-1">Étape {{ index + 1 }}</p>
                                <h5 class="fs-14 fw-semibold mb-1">{{ step.title }}</h5>
                                <p class="text-muted fs-12 mb-0">{{ step.text }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="card">
                <div class="card-header">
                    <form>
                        <div class="row g-3">
                            <div class="col-xxl-5 col-sm-12">
                                <div class="search-box">
                                    <input type="search" class="form-control search" placeholder="Rechercher un client, un e-mail, un n° de fiche..." v-model="query" @input="debouncedSearch" id="searchHubSpotSyncIssues">
                                    <i class="ri-search-line search-icon"></i>
                                </div>
                            </div>

                            <form-select-field
                                name="filters.status"
                                label="Statut"
                                :label-hidden="true"
                                @update:field="filters.status = $event"
                                :errors="errors"
                                :required="true"
                                :data="filters.status"
                                bs-class="col-xxl-3 col-sm-4"
                                additional-class="bg-light border-light"
                                :liste-options="[
                                    { text: 'À traiter', value: 'open' },
                                    { text: 'Traités', value: 'resolved' },
                                ]"
                                margin="mb-0"
                            />

                            <form-select-field
                                name="filters.type"
                                label="Type"
                                :label-hidden="true"
                                @update:field="filters.type = $event"
                                :errors="errors"
                                :required="true"
                                :data="filters.type"
                                bs-class="col-xxl-3 col-sm-4"
                                additional-class="bg-light border-light"
                                placeholder="Tous les types"
                                :liste-options="typeOptions"
                                margin="mb-0"
                            />

                            <div class="col-xxl-1 col-sm-4">
                                <button type="button" class="btn w-100" @click.prevent="applyFilter" :class="filtersApplied ? 'btn-dark' : 'btn-warning'">
                                    <i class="ri-equalizer-fill me-1 align-bottom"></i>
                                    Filtrer
                                </button>
                            </div>
                        </div>
                    </form>
                </div>

                <div class="card-body">
                    <data-table
                        :columns="columns"
                        :data="issues"
                        :loading="loading"
                        :links="links"
                        :meta="meta"
                        :sorting-field="sortingField"
                        :sorting-order="sortingFieldsOrder"
                        :search-loaded="searchLoaded"
                        @sort-changed="changeSorting"
                        @page-changed="loadData"
                    >
                        <!-- Fiche -->
                        <template #id="{ item }">
                            <span class="fw-medium text-dark">n° {{ item.data.client.id }}</span>
                        </template>

                        <!-- Client -->
                        <template #client="{ item }">
                            <div class="text-dark">{{ item.data.client.full_name }}</div>
                            <div class="text-muted fs-12">{{ item.data.client.email }}</div>
                            <span class="badge fs-11 bg-dark-subtle text-dark mt-1" v-if="item.data.client.deleted">Fiche supprimée</span>
                        </template>

                        <!-- Type -->
                        <template #type="{ item }">
                            <span :class="'badge fs-11 bg-' + item.data.type_badge_color + '-subtle text-' + item.data.type_badge_color">{{ item.data.type_label }}</span>
                        </template>

                        <!-- Ce qui se passe -->
                        <template #summary="{ item }">
                            <div class="text-dark">{{ item.data.summary }}</div>
                            <div class="d-flex align-items-center gap-2 text-muted fs-12 mt-1" v-if="!item.data.resolved_at_fr">
                                <i class="ri-lightbulb-line flex-shrink-0 fs-14"></i><span>{{ item.data.how_to_fix }}</span>
                            </div>
                            <div class="d-flex align-items-center gap-2 text-success fs-12 mt-1" v-else>
                                <i class="ri-checkbox-circle-line flex-shrink-0 fs-14"></i>Traité le {{ item.data.resolved_at_fr }}{{ item.data.resolved_by ? ' par ' + item.data.resolved_by : ', par une synchro réussie' }}
                            </div>
                        </template>

                        <!-- Dernière fois -->
                        <template #last_seen_at="{ item }">
                            {{ item.data.last_seen_at_fr }}
                        </template>

                        <!-- Détails -->
                        <template #details="{ item }">
                            <button type="button" class="btn btn-secondary bg-gradient waves-effect waves-light btn-sm" @click="showDetails({ ...item.data, can_update: item.can.update })">
                                Voir
                            </button>
                        </template>

                        <!-- Actions -->
                        <template #actions="{ item }">
                            <router-link :to="{ name: 'clients.show', params: { id: item.data.client.id } }" class="text-dark" title="Ouvrir la fiche client">
                                <i class="ri-contacts-book-line fs-17 px-1"></i>
                            </router-link>
                            <template v-if="item.can.update && !item.data.resolved_at_fr">
                                <a href="#" class="text-info" title="Relancer la synchro" :class="{ 'pe-none opacity-50': retrying }" @click.prevent="retry(item.data.id)">
                                    <i class="ri-refresh-line fs-17 px-1"></i>
                                </a>
                                <a href="#" class="text-success" title="Marquer comme traité" :class="{ 'pe-none opacity-50': resolving }" @click.prevent="resolve(item.data.id)">
                                    <i class="ri-checkbox-circle-line fs-17 px-1"></i>
                                </a>
                            </template>
                            <template v-else>
                                <i class="ri-refresh-line fs-17 px-1 text-muted"></i>
                                <i class="ri-checkbox-circle-line fs-17 px-1 text-muted"></i>
                            </template>
                        </template>
                    </data-table>
                </div>
            </div>
        </div>
    </div>

    <modal-hub-spot-sync-issue-details
        modal-id="modalHubSpotSyncIssue"
        :issue="selectedIssue"
        :retrying="retrying"
        :resolving="resolving"
        @retry="retryFromModal"
        @resolve="resolveFromModal"
    />
</template>

<style scoped>

</style>
